import type { ReactNode } from 'react'
import { cn } from '@/helpers/utils'

export interface ChipProps {
	label: string
	isActive?: boolean
	onClick?: () => void
	startSlot?: ReactNode
	className?: string
	endSlot?: ReactNode
	variant?: 'success' | 'info' | 'neutral' | 'danger' | 'warning'
	size?: 'sm' | 'md'
	shape?: 'circle' | 'rectangle' | 'roundedRectangle'
	isClickable?: boolean
}

const variantStyles = {
	success: {
		active: 'text-success-700 border-success-200 bg-success-50',
		inactive: 'text-neutral-700 border-neutral-300 bg-neutral-white'
	},
	info: {
		active: 'text-blue-700 border-blue-200 bg-blue-50',
		inactive: 'text-blue-700 border-neutral-300 bg-neutral-white'
	},
	neutral: {
		active: 'text-neutral-700 border-neutral-200 bg-neutral-50',
		inactive: 'text-neutral-700 border-neutral-200 bg-neutral-white'
	},
	danger: {
		active: 'text-danger-700 border-danger-200 bg-danger-50',
		inactive: 'text-neutral-700 border-neutral-300 bg-neutral-white'
	},
	warning: {
		active: 'text-warning-700 border-warning-200 bg-warning-50',
		inactive: 'text-neutral-700 border-neutral-300 bg-neutral-white'
	}
}

const shapeStyles = {
	rectangle: 'rounded-md',
	roundedRectangle: 'rounded-[14px]',
	circle: 'rounded-[9px]'
}

const sizeStyles = {
	sm: 'text-xs min-h-[26px] [&_svg]:w-3.5 [&_svg]:h-3.5',
	md: 'text-sm min-h-[32px] [&_svg]:w-4 [&_svg]:h-4'
}

export const Chip = ({
	label,
	className,
	startSlot,
	endSlot,
	onClick,
	isActive = false,
	variant = 'info',
	size = 'sm',
	isClickable,
	shape = 'rectangle'
}: ChipProps) => {
	const props = {
		className: cn(
			'flex items-center gap-1 overflow-hidden border px-1 py-0.5 font-medium',
			shapeStyles[shape],
			sizeStyles[size],
			isActive
				? variantStyles[variant].active
				: variantStyles[variant].inactive,
			(isClickable || onClick) && 'cursor-pointer',
			className
		),
		title: label
	}
	if (onClick) {
		return (
			<button onClick={onClick} {...props} type='button'>
				{startSlot}
				<span className='block overflow-hidden text-ellipsis px-1'>
					{label}
				</span>
				{endSlot}
			</button>
		)
	}
	return (
		<div {...props}>
			{startSlot}
			<span className='block overflow-hidden text-ellipsis px-1'>
				{label}
			</span>

			{endSlot}
		</div>
	)
}

Chip.displayName = 'Chip'
